import React, { useEffect, useState, useCallback, useMemo, Suspense } from 'react';
import {
  Plug, Plus, Pencil, Trash2, ToggleLeft, ToggleRight,
  Loader2, Play, Save, X, ChevronDown, AlertCircle, Activity,
  AlertTriangle, BookOpen, Bot, Sparkles, Info, Link2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { integrationsAPI, agentsAPI } from '../services/api';
import type { DataConnector, ConnectorPayload, InputParam, IdentityClaim } from '../services/api';
import type { RenderAs, CardMapping, CardMappingSource } from '../types/connectorCards';
import type { Agent } from '../types';
import type { ProductCardData } from '../utils/productBlocks';
import type { OrderCardData } from '../utils/orderBlocks';
import {
  extractItems, extractPlaceholders, isAbsoluteHttpUrl, renderUrlTemplate,
  resolvePath, resolveProductUrl,
} from '../utils/connectorCardUrl';
import ProductTile from '../components/Widget/ProductTile';
import OrderCard from '../components/Widget/OrderCard';
import { useToast } from '../contexts/ToastContext';
import usePermissions from '../hooks/usePermissions';
import { useTheme } from '../contexts/ThemeContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useBilling } from '../contexts/BillingContext';
import { useBillingMode } from '../hooks/useBillingMode';
import ConfirmModal from '../components/ConfirmModal';
import { serverIsoToMillis, NO_DATE } from '../utils/datetime';
import ConnectShopifyCta from '../components/integrations/ConnectShopifyCta';
import SpagoIntegrationCard from '../components/integrations/SpagoIntegrationCard';
import ShopDomainModal from '../components/shopify/ShopDomainModal';
import { shopifyAPI } from '../services/api';
import { SUPPORT_MAILTO } from '../config/support';

// Plan-AutoInstallCommerceAgent §5b (2026-05-26) — Shopify-build status
// card replaces the previous per-agent ShopifyIntegrationsBlock import.
// Lazy-loaded so the Stripe bundle never pulls the module (Vite tree-
// shakes against `VITE_BILLING_PROVIDER` at build time, same trick as
// the prior ShopifyIntegrationsBlock gating in Plan-ShopifyIsolatedFrontend §3.1).
const ShopifyStatusCard =
  import.meta.env.VITE_BILLING_PROVIDER === 'shopify'
    ? React.lazy(() => import('../components/integrations/ShopifyStatusCard'))
    : null;

const IS_SHOPIFY_BUILD = import.meta.env.VITE_BILLING_PROVIDER === 'shopify';

// Connector rows installed and managed by an integration package. They are
// agent tools, not user config: hidden from the custom connector list and never
// opened in the editor (a hand edit would be overwritten by the next package
// update; the backend also refuses such edits with a 409). The Spago card
// controls its own rows.
const MANAGED_CONNECTOR_SOURCES = new Set(['shopify_template', 'spago_template']);
const isManagedConnector = (c: Pick<DataConnector, 'source'>) =>
  !!c.source && MANAGED_CONNECTOR_SOURCES.has(c.source);

// ─── helpers ────────────────────────────────────────────────────────────────

function timeAgo(iso: string | null | undefined): string {
  const ms = serverIsoToMillis(iso);
  if (ms === null) return NO_DATE;
  const diff = Date.now() - ms;
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const METHOD_COLORS: Record<string, string> = {
  GET: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400',
  POST: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400',
  PUT: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400',
};

// `POST /infer-card-mapping` proposes this literal host when the item exposes a
// handle but no URL — inference cannot know the storefront domain. Backend
// `validate_card_mapping` rejects it by design; the UI prompts for the real host.
const UNKNOWN_HOST_SENTINEL = '<UNKNOWN_HOST>';

const IDENTITY_CLAIM_OPTIONS: { value: IdentityClaim; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'user_id', label: 'User ID' },
  { value: 'name', label: 'Name' },
  { value: 'mkt', label: 'Organisation or marketplace ID' },
];

const EMPTY_PARAM: InputParam = {
  name: '', type: 'string', required: false, description: '',
  source: 'customer', ask_prompt: '', example: '', static_value: '',
};

// Brand color for the card-mapping preview only. At runtime the chat widget
// injects the agent's configured widget color; here we just need a sensible
// stand-in that matches the app's primary indigo.
const PREVIEW_BRAND_COLOR = '#6366f1';

const RENDER_AS_LABELS: Record<Exclude<RenderAs, 'none'>, string> = {
  product_cards: 'Product cards',
  order_cards: 'Order cards',
};

// ─── component ──────────────────────────────────────────────────────────────

const Integrations: React.FC = () => {
  const { showToast } = useToast();
  // Replaces the inline `user?.roles?.some(r => ['admin','agents_manager'].includes(r))`
  // that used to live inside the no-agents empty state below.
  const { canManageAgents } = usePermissions();
  const { activeWorkspace } = useWorkspace();
  // Plan-AutoInstallCommerceAgent (Storm 439407b 2026-05-26): the
  // `shopify_agent_id` pointer + per-agent `needs_onboarding` flags
  // live on the billing payload, not on the workspace or agents
  // endpoints. We branch the status card / Continue-setup CTA off
  // `billing.shopify_agent_id`.
  const { billing } = useBilling();
  const showLegacyCredits = useBillingMode() === 'legacy';

  // Plan-AutoInstallCommerceAgent §5c (2026-05-26) — kicked when the
  // "Continue setup" fallback CTA is clicked. Triggers OAuth without an
  // `agent_id` so the backend resolver assigns the designated agent.
  // Plan-ShopifyIntegrationsCleanup.md §C — the legacy window.prompt was
  // replaced by <ShopDomainModal>. The modal owns its own submitting/error
  // state, so we only need a `shopModalOpen` boolean here.
  const [shopModalOpen, setShopModalOpen] = useState(false);
  const handleContinueSetup = () => setShopModalOpen(true);
  const handleShopSubmit = async (shop: string) => {
    // shopifyAPI.getAuthUrl redirects via window.location.href on success,
    // so this Promise effectively never resolves on the happy path. On
    // failure we re-throw so the modal can render the error inline.
    await shopifyAPI.getAuthUrl(shop);
  };

  // Agent selector state
  const [selectedAgentId, setSelectedAgentId] = useState<string>('');

  // Shopify-data-connector filter — Plan-ShopifyIsolatedFrontend.md §3.1.
  // Toggle button was retired with the legacy ShopifyIntegrationsBlock
  // (Plan-AutoInstallCommerceAgent §5b); state is kept at default `false`
  // so the connector grid below keeps its current "show all" behavior.
  const [shopifyFilter] = useState(false);

  // List state
  const [connectors, setConnectors] = useState<DataConnector[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<DataConnector | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // Agents list (for scope selector)
  const [agents, setAgents] = useState<Agent[]>([]);

  // Form/panel state
  const [showForm, setShowForm] = useState(false);
  const [editingConnector, setEditingConnector] = useState<DataConnector | null>(null);
  const [savedConnector, setSavedConnector] = useState<DataConnector | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  // Form fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [method, setMethod] = useState<'GET' | 'POST' | 'PUT'>('GET');
  const [authType, setAuthType] = useState<'none' | 'bearer' | 'api_key' | 'basic'>('none');
  const [authValue, setAuthValue] = useState('');
  const [headerName, setHeaderName] = useState('X-Api-Key');
  const [basicUsername, setBasicUsername] = useState('');
  const [basicPassword, setBasicPassword] = useState('');
  const [authReset, setAuthReset] = useState(false);
  const [authSource, setAuthSource] = useState<'encrypted' | 'input_param'>('encrypted');
  const [authParamName, setAuthParamName] = useState('');
  const [inputParams, setInputParams] = useState<InputParam[]>([]);
  const [customHeaders, setCustomHeaders] = useState<{ name: string; value: string }[]>([]);
  const [savedResponse, setSavedResponse] = useState<any>(null);
  const [useSavedResponse, setUseSavedResponse] = useState(false);
  const [bodyType, setBodyType] = useState<'none' | 'json' | 'form'>('none');
  const [bodyTemplate, setBodyTemplate] = useState('');
  const [returnsDescription, setReturnsDescription] = useState('');
  const [dependsOn, setDependsOn] = useState('');
  const [openAdvancedParams, setOpenAdvancedParams] = useState<Set<number>>(new Set());
  const [agentScope, setAgentScope] = useState<'all' | 'selected'>('all');
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([]);
  // Identity passthrough — per-connector opt-in, default OFF. A partner token
  // must never reach a connector that has not explicitly enabled forwarding.
  const [forwardIdentityToken, setForwardIdentityToken] = useState(false);
  // Blank = use the backend's standard header (never prefilled with a partner name).
  const [identityHeaderName, setIdentityHeaderName] = useState('');

  // Test playground state
  const [testInputs, setTestInputs] = useState<Record<string, string>>({});
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState<{ status_code: number; data: Record<string, unknown> } | null>(null);
  const [projectionFields, setProjectionFields] = useState<string[]>([]);
  const [projectionSaving, setProjectionSaving] = useState(false);

  // Card rendering state — Plan-ConnectorCardRendering.md
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [renderAs, setRenderAs] = useState<RenderAs>('none');
  const [cardMapping, setCardMapping] = useState<CardMapping | null>(null);
  const [cardMappingSource, setCardMappingSource] = useState<CardMappingSource | null>(null);
  const [inferLoading, setInferLoading] = useState(false);
  const [inferHint, setInferHint] = useState<string | null>(null);
  const [inferWarnings, setInferWarnings] = useState<string[]>([]);
  const [sampleCards, setSampleCards] = useState<Record<string, unknown>[]>([]);
  const [mappingJson, setMappingJson] = useState('');
  const [mappingJsonError, setMappingJsonError] = useState<string | null>(null);
  const [showMappingJson, setShowMappingJson] = useState(false);

  // Identity verification keys used to live here as their own card. They are
  // now collected by the partner integration that needs them — the Spago card
  // asks for the Key ID + identity secret on connect and replaces/removes them
  // on replace-key/disconnect — so there is no second, partner-agnostic place
  // to add a key that would be wrong for every partner but one (the token's
  // `iss` must match the key's issuer). `/api/integrations/identity-keys`
  // still exists for recovery and for the next partner package.

  // ── load ──────────────────────────────────────────────────────────────────

  const loadConnectors = useCallback(async () => {
    try {
      const res = await integrationsAPI.list();
      setConnectors(res.data);
    } catch {
      showToast('Failed to load connectors.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadConnectors();
    agentsAPI.list().then((res) => {
      const list: Agent[] = res.data ?? [];
      setAgents(list);
      if (list.length === 0) setLoading(false);
    }).catch(() => { setLoading(false); });
  }, [loadConnectors]);

  // The Shopify status load + ?shopify=connected redirect handler used
  // to live here; both have moved into <ShopifyIntegrationsBlock> so
  // they only fire on the Shopify build. See Plan-ShopifyIsolatedFrontend.md
  // §3.1 — the Stripe bundle must not initiate any Shopify network calls.

  // ── form helpers ──────────────────────────────────────────────────────────

  const resetForm = () => {
    setName(''); setDescription(''); setEndpoint(''); setMethod('GET');
    setAuthType('none'); setAuthValue(''); setHeaderName('X-Api-Key');
    setBasicUsername(''); setBasicPassword(''); setAuthReset(false);
    setAuthSource('encrypted'); setAuthParamName('');
    setBodyType('none'); setBodyTemplate('');
    setReturnsDescription(''); setDependsOn(''); setOpenAdvancedParams(new Set());
    setInputParams([]); setCustomHeaders([]); setSavedResponse(null); setUseSavedResponse(false);
    setAgentScope('all'); setSelectedAgentIds([]);
    setForwardIdentityToken(false); setIdentityHeaderName('');
    setTestInputs({}); setTestResult(null); setProjectionFields([]);
    setSavedConnector(null);
    setRenderAs('none'); setCardMapping(null); setCardMappingSource(null);
    setInferHint(null); setInferWarnings([]); setSampleCards([]);
    setMappingJson(''); setMappingJsonError(null); setShowMappingJson(false);
  };

  const openNew = () => {
    resetForm();
    setEditingConnector(null);
    setShowForm(true);
  };

  const openEdit = (c: DataConnector) => {
    // Defensive — managed rows never reach the list, but refuse them anyway.
    if (isManagedConnector(c)) {
      showToast("This connector is managed by an integration. Change it from that integration's card.", 'error');
      return;
    }
    setEditingConnector(c);
    setName(c.name);
    setDescription(c.description);
    setEndpoint(c.endpoint);
    setMethod(c.method);
    setAuthType(c.auth_type);
    setHeaderName(c.auth_header_name || 'X-Api-Key');
    setAuthValue('');
    setBasicUsername('');
    setBasicPassword('');
    setAuthReset(false);
    setAuthSource(c.auth_source ?? 'encrypted');
    setAuthParamName(c.auth_param_name ?? '');
    setBodyType(c.body_type ?? 'none');
    setBodyTemplate(c.body_template ?? '');
    setReturnsDescription(c.returns_description ?? '');
    setDependsOn(c.depends_on ?? '');
    setOpenAdvancedParams(new Set());
    setInputParams(c.input_params.length ? c.input_params : []);
    setCustomHeaders(c.custom_headers || []);
    setSavedResponse(c.saved_response || null);
    setUseSavedResponse(c.use_saved_response || false);
    setAgentScope(c.agent_scope);
    setSelectedAgentIds(c.agent_ids || []);
    setForwardIdentityToken(c.forward_identity_token === true);
    // A connector's STORED header is data — show it as saved.
    setIdentityHeaderName(c.identity_header_name ?? '');
    setProjectionFields(c.projection);
    setTestInputs({});
    setTestResult(null);
    setRenderAs(c.render_as ?? 'none');
    setCardMapping(c.card_mapping ?? null);
    setCardMappingSource(c.card_mapping_source ?? null);
    setMappingJson(c.card_mapping ? JSON.stringify(c.card_mapping, null, 2) : '');
    setMappingJsonError(null);
    setShowMappingJson(false);
    setInferHint(null);
    setInferWarnings([]);
    setSampleCards([]);
    setSavedConnector(c);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingConnector(null);
    resetForm();
  };

  // ── save ──────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!name.trim()) { showToast('Name is required.', 'error'); return; }
    if (!endpoint.trim()) { showToast('Endpoint URL is required.', 'error'); return; }

    const payload: ConnectorPayload = {
      name: name.trim(),
      description: description.trim(),
      endpoint: endpoint.trim(),
      method,
      auth_type: authType,
      auth_header_name: authType === 'api_key' ? headerName : 'Authorization',
      auth_source: authSource,
      auth_param_name: authParamName.trim() || null,
      body_type: bodyType,
      body_template: bodyType !== 'none' && bodyTemplate.trim() ? bodyTemplate.trim() : null,
      returns_description: returnsDescription.trim() || null,
      depends_on: dependsOn.trim() || null,
      input_params: inputParams.filter(p => p.name.trim()),
      custom_headers: customHeaders.filter(h => h.name.trim()),
      saved_response: savedResponse,
      use_saved_response: useSavedResponse,
      projection: projectionFields,
      agent_scope: agentScope,
      agent_ids: agentScope === 'selected' ? selectedAgentIds : [],
      forward_identity_token: forwardIdentityToken,
      is_active: editingConnector?.is_active ?? true,
      render_as: renderAs,
      card_mapping: renderAs !== 'none' ? cardMapping : null,
      card_mapping_source: renderAs !== 'none' && cardMapping ? cardMappingSource : null,
    };

    // Blank header name ⇒ OMIT the key. On create the backend default applies;
    // on update an absent key means "unchanged". Never send "" (it would be
    // stored empty or rejected by the validator).
    const identityHeader = identityHeaderName.trim();
    if (identityHeader) payload.identity_header_name = identityHeader;

    // Build auth_value string
    if (authType === 'bearer' && authValue) {
      payload.auth_value = authValue;
    } else if (authType === 'api_key' && authValue) {
      payload.auth_value = authValue;
    } else if (authType === 'basic' && (basicUsername || basicPassword)) {
      payload.auth_value = btoa(`${basicUsername}:${basicPassword}`);
    }

    setFormLoading(true);
    try {
      let saved: DataConnector;
      if (editingConnector) {
        const res = await integrationsAPI.update(editingConnector._id, payload);
        saved = res.data;
        setConnectors(prev => prev.map(c => c._id === saved._id ? saved : c));
        showToast('Connector updated.', 'success');
      } else {
        const res = await integrationsAPI.create(payload);
        saved = res.data;
        setConnectors(prev => [...prev, saved]);
        showToast('Connector created.', 'success');
        setEditingConnector(saved);
      }
      setSavedConnector(saved);
      setProjectionFields(saved.projection);
      setAuthReset(false);
      setAuthValue('');
      setBasicUsername('');
      setBasicPassword('');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to save connector.';
      showToast(msg, 'error');
    } finally {
      setFormLoading(false);
    }
  };

  // Shopify connect/disconnect handlers moved to
  // <ShopifyIntegrationsBlock> — the parent only forwards an
  // onAfterDisconnect callback so the shared connector grid below
  // refreshes when a store is removed.

  // ── toggle ────────────────────────────────────────────────────────────────

  const handleToggle = async (c: DataConnector) => {
    setTogglingId(c._id);
    const next = !c.is_active;
    setConnectors(prev => prev.map(x => x._id === c._id ? { ...x, is_active: next } : x));
    try {
      await integrationsAPI.toggle(c._id, next);
    } catch {
      setConnectors(prev => prev.map(x => x._id === c._id ? { ...x, is_active: c.is_active } : x));
      showToast('Failed to toggle connector.', 'error');
    } finally {
      setTogglingId(null);
    }
  };

  // ── delete ────────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await integrationsAPI.delete(deleteTarget._id);
      setConnectors(prev => prev.filter(c => c._id !== deleteTarget._id));
      showToast('Connector deleted.', 'success');
      if (editingConnector?._id === deleteTarget._id) closeForm();
    } catch {
      showToast('Failed to delete connector.', 'error');
    } finally {
      setDeleteTarget(null);
    }
  };

  // ── test ──────────────────────────────────────────────────────────────────

  const handleTest = async () => {
    if (!savedConnector) return;
    setTestLoading(true);
    setTestResult(null);
    try {
      const res = await integrationsAPI.test(savedConnector._id, testInputs);
      setTestResult(res.data);
      // Auto-select keys not yet in projection
      if (!projectionFields.length) {
        setProjectionFields(flattenResponsePaths(res.data.data ?? {}));
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Test failed.';
      showToast(msg, 'error');
    } finally {
      setTestLoading(false);
    }
  };

  // ── projection save ───────────────────────────────────────────────────────

  const handleSaveProjection = async () => {
    if (!savedConnector) return;
    setProjectionSaving(true);
    try {
      const res = await integrationsAPI.saveProjection(savedConnector._id, projectionFields);
      setSavedConnector(res.data);
      setConnectors(prev => prev.map(c => c._id === res.data._id ? res.data : c));
      showToast('Projection saved.', 'success');
    } catch {
      showToast('Failed to save projection.', 'error');
    } finally {
      setProjectionSaving(false);
    }
  };

  // ── card mapping (Plan-ConnectorCardRendering.md) ─────────────────────────

  const handleGenerateMapping = async () => {
    if (!savedConnector || renderAs === 'none') return;
    setInferLoading(true);
    setInferHint(null);
    try {
      const res = await integrationsAPI.inferCardMapping(savedConnector._id, renderAs);
      setCardMapping(res.data.proposed_mapping);
      setCardMappingSource('inferred');
      setMappingJson(JSON.stringify(res.data.proposed_mapping, null, 2));
      setMappingJsonError(null);
      setSampleCards(res.data.sample_cards ?? []);
      setInferWarnings(res.data.warnings ?? []);
      if (!(res.data.sample_cards ?? []).length) {
        setInferHint('The mapping produced no sample cards — check that the saved response contains items, or adjust the mapping below.');
      }
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { detail?: string } } };
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 400) {
        // Backend has no saved sample response for this connector yet.
        setInferHint(detail || 'Run Test first — the mapping is generated from a saved sample response.');
      } else {
        showToast(detail || 'Failed to generate mapping.', 'error');
      }
    } finally {
      setInferLoading(false);
    }
  };

  const handleMappingJsonChange = (text: string) => {
    setMappingJson(text);
    try {
      const parsed = JSON.parse(text) as Partial<CardMapping>;
      if (!parsed || typeof parsed !== 'object' || typeof parsed.items_path !== 'string'
          || !parsed.fields || typeof parsed.fields !== 'object' || Array.isArray(parsed.fields)) {
        setMappingJsonError('Mapping must be an object with "items_path" (string) and "fields" (object).');
        return;
      }
      setCardMapping({
        items_path: parsed.items_path,
        fields: parsed.fields as Record<string, string>,
        // Carried through explicitly: rebuilding the mapping from only the two
        // required keys silently dropped `product_url_template` (and any future
        // optional key) the moment anyone opened the advanced editor.
        ...(typeof parsed.product_url_template === 'string' && parsed.product_url_template
          ? { product_url_template: parsed.product_url_template }
          : {}),
      });
      setCardMappingSource('manual');
      setMappingJsonError(null);
    } catch {
      setMappingJsonError('Invalid JSON — the last valid mapping is kept until this parses.');
    }
  };

  /** Product page URL template — Plan-ConnectorProductUrlTemplate.md. Lives
   *  inside card_mapping, so it needs the advanced JSON textarea kept in sync. */
  const handleProductUrlTemplateChange = (value: string) => {
    if (!cardMapping) return;
    const next: CardMapping = { ...cardMapping };
    if (value.trim()) next.product_url_template = value.trim();
    else delete next.product_url_template;
    setCardMapping(next);
    setMappingJson(JSON.stringify(next, null, 2));
    setMappingJsonError(null);
    setCardMappingSource('manual');
  };

  // ── product URL template preview ───────────────────────────────────────────
  // Resolved locally against the connector's saved_response so the owner sees
  // the URL the backend WILL build, without paying for a re-inference.

  const urlTemplate = cardMapping?.product_url_template ?? '';

  const rawItems = useMemo(
    () => (cardMapping ? extractItems(savedResponse, cardMapping.items_path) : []),
    [savedResponse, cardMapping],
  );
  const sampleItem: Record<string, unknown> | undefined = rawItems[0];
  const sampleItemFields = useMemo(() => (sampleItem ? Object.keys(sampleItem) : []), [sampleItem]);

  /** null when there is nothing to say; otherwise a resolved URL or a reason. */
  const urlTemplatePreview = useMemo((): { ok: boolean; text: string } | null => {
    if (!urlTemplate.trim()) return null;
    // "Generate mapping" deliberately proposes this sentinel when the API only
    // exposes a handle — the host is the one thing inference cannot know. The
    // connector PUT rejects it, so say so before the owner hits Save.
    if (urlTemplate.includes(UNKNOWN_HOST_SENTINEL)) {
      return {
        ok: false,
        text: `Replace ${UNKNOWN_HOST_SENTINEL} with your storefront's domain (e.g. https://yourstore.com/…). The connector cannot be saved until you do.`,
      };
    }
    if (!isAbsoluteHttpUrl(urlTemplate, { allowPlaceholders: true })) {
      return {
        ok: false,
        text: 'Must be an absolute http:// or https:// URL with a real host, and cannot contain spaces or < > " \' ` \\ ^ | characters.',
      };
    }
    if (!sampleItem) return null;
    const rendered = renderUrlTemplate(sampleItem, urlTemplate);
    if (!rendered) {
      const missing = extractPlaceholders(urlTemplate)
        .filter(p => {
          const v = resolvePath(sampleItem, p);
          return v === undefined || v === null || v === '';
        });
      return {
        ok: false,
        text: missing.length
          ? `No value for ${missing.map(p => `{${p}}`).join(', ')} on the sample item — the card will render without a link.`
          : 'The template did not resolve to a valid URL.',
      };
    }
    return { ok: true, text: rendered };
  }, [urlTemplate, sampleItem]);

  /** `fields.url` is mapped but yields a handle, not a URL — the spago case. */
  const mappedUrlIsNotAbsolute = Boolean(
    cardMapping?.fields?.url && sampleItem
    && !isAbsoluteHttpUrl(resolvePath(sampleItem, cardMapping.fields.url)),
  );

  const showsNoLinkWarning = Boolean(
    renderAs === 'product_cards' && cardMapping
    && !cardMapping.fields?.url && !cardMapping.product_url_template,
  );

  /**
   * The backend's `sample_cards` were built before the template was typed, so
   * re-resolve `checkout_url` here to keep the ProductTile preview honest.
   * Cards can be dropped/de-duplicated server-side, so only re-resolve when the
   * two lists line up 1:1 — otherwise trust what the backend sent.
   */
  const previewCards = useMemo(() => {
    if (renderAs !== 'product_cards' || !cardMapping || rawItems.length !== sampleCards.length) {
      return sampleCards;
    }
    return sampleCards.map((card, i) => ({
      ...card,
      checkout_url:
        resolveProductUrl(rawItems[i], cardMapping.fields, cardMapping.product_url_template) ?? undefined,
    }));
  }, [renderAs, cardMapping, rawItems, sampleCards]);

  // ── param helpers ─────────────────────────────────────────────────────────

  const updateParam = (idx: number, field: keyof InputParam, value: string | boolean) => {
    setInputParams(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };

  const removeParam = (idx: number) => {
    setInputParams(prev => prev.filter((_, i) => i !== idx));
  };

  const toggleAgentId = (id: string) => {
    const agent = agents.find(a => a.id === id);
    const altId = agent?.agent_id;
    setSelectedAgentIds(prev => {
      const has = prev.includes(id) || (altId ? prev.includes(altId) : false);
      if (has) return prev.filter(x => x !== id && x !== altId);
      return [...prev, id];
    });
  };

  const toggleProjectionField = (key: string) => {
    setProjectionFields(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  // ── render ────────────────────────────────────────────────────────────────

  const flattenResponsePaths = (obj: unknown, prefix = '', depth = 0): string[] => {
    if (depth > 2 || typeof obj !== 'object' || obj === null) {
      return prefix ? [prefix] : [];
    }
    if (Array.isArray(obj)) {
      if (obj.length === 0 || typeof obj[0] !== 'object' || obj[0] === null) {
        return prefix ? [prefix] : [];
      }
      return flattenResponsePaths(obj[0], prefix, depth);
    }
    const paths: string[] = [];
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      const path = prefix ? `${prefix}.${key}` : key;
      if (typeof value === 'object' && value !== null) {
        paths.push(...flattenResponsePaths(value, path, depth + 1));
      } else {
        paths.push(path);
      }
    }
    return paths;
  };

  const showAuth = authType !== 'none';
  const testResponseKeys = testResult?.data ? flattenResponsePaths(testResult.data) : [];

  // Filtered connectors by selected agent
  const filteredConnectors = connectors.filter(c => {
    // Integration-package rows (Shopify, Spago) are agent tools, not user config.
    if (isManagedConnector(c)) return false;
    if (!selectedAgentId) return true;
    const agent = agents.find(a => a.agent_id === selectedAgentId);
    const ids = [selectedAgentId, ...(agent?.id && agent.id !== selectedAgentId ? [agent.id] : [])];
    return c.agent_scope === 'all' || (c.agent_scope === 'selected' && (c.agent_ids || []).some(id => ids.includes(id)));
  });

  // No-agent empty state
  if (!loading && agents.length === 0) {
    // Hoisted to the hook above — identical policy to HelpCenterStudio's.
    return (
      <div className="flex items-center justify-center" style={{ height: 'calc(100dvh - 8rem)' }}>
        <div className="glass rounded-2xl p-12 text-center max-w-md">
          <Bot className="w-14 h-14 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No AI Agents yet</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            Create an AI Agent first, then configure its data connectors.
          </p>
          {canManageAgents && (
            <Link
              to="/agents"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:from-indigo-600 hover:to-purple-700 transition-colors"
            >
              <Bot className="w-4 h-4" />
              Go to AI Agents
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <h1 className="text-3xl font-bold gradient-text">Data Connectors</h1>
            {/* Agent selector */}
            {agents.length > 0 && (
              <select
                value={selectedAgentId}
                onChange={e => setSelectedAgentId(e.target.value)}
                className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full sm:w-auto"
              >
                <option value="">All Agents</option>
                {agents.map(a => (
                  <option key={a.agent_id} value={a.agent_id}>{a.config.agent_name}</option>
                ))}
              </select>
            )}
          </div>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Connect your APIs as live tools for your AI agents.
          </p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <Link to="/integrations/docs" className="btn-secondary flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            View Guide
          </Link>
          <button onClick={openNew} className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" />
            New Connector
          </button>
        </div>
      </div>

      {/* Plan-AutoInstallCommerceAgent §5b/§5c (2026-05-26) — on the
          Shopify build, the per-agent ShopifyIntegrationsBlock is
          replaced by the one-workspace-one-store status card (post-
          OAuth) or a single Continue-setup CTA fallback (edge case for
          workspaces that never completed OAuth). The Stripe build is
          unchanged: ConnectShopifyCta (marketing link to
          shop.prodagi.ai). The reloadKey + shopifyFilter machinery
          below is kept inert on the Shopify build — Phase B does not
          regress the legacy "View Connectors" filter for Stripe. */}
      {IS_SHOPIFY_BUILD && ShopifyStatusCard ? (
        billing?.shopify_agent_id && activeWorkspace ? (
          <Suspense
            fallback={
              <div className="card p-6 flex justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
              </div>
            }
          >
            <ShopifyStatusCard
              workspaceId={activeWorkspace.id}
              shopifyShopDomain={billing?.shopify_shop_domain}
              onAfterReassign={() => {
                loadConnectors();
              }}
            />
          </Suspense>
        ) : (
          // Continue-setup fallback — workspaces that landed without
          // completing OAuth (direct nav, aborted flow, manual ws
          // creation). No agent picker; backend resolver decides.
          <div className="card p-6 space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center shrink-0">
                <BookOpen className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100">
                  Continue Shopify setup
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Finish connecting your Shopify store to your AI agent.
                </p>
              </div>
              <button
                onClick={handleContinueSetup}
                className="btn-primary text-xs flex items-center justify-center gap-1.5 self-start sm:self-auto w-full sm:w-auto"
              >
                <Plug className="w-3.5 h-3.5" />
                Continue setup
              </button>
            </div>
          </div>
        )
      ) : (
        <ConnectShopifyCta />
      )}

      {/* Spago integration package — Plan-SpagoIntegration.md §F. Package rows
          are hidden from the list below and controlled inside this card.
          App build only: Shopify-build workspaces are Shopify merchants. And
          only ONE workspace ever sees it: the card renders nothing unless the
          backend's SPAGO_WORKSPACE_ID names the active workspace. */}
      {!IS_SHOPIFY_BUILD && (
        <SpagoIntegrationCard
          agents={agents}
          onConnectorsChanged={loadConnectors}
        />
      )}

      {/* Connector List */}
      {loading ? (
        <div className="card p-8 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : filteredConnectors.length === 0 ? (
        <div className="card p-12 flex flex-col items-center text-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-purple-500/20 flex items-center justify-center">
            <Plug className="w-8 h-8 text-primary" />
          </div>
          <div>
            <p className="font-semibold text-gray-800 dark:text-gray-200">No connectors yet.</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Add your first API connector to get started.</p>
          </div>
          <button onClick={openNew} className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" />
            New Connector
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {(shopifyFilter ? filteredConnectors.filter(c => c.name.startsWith('shopify_')) : filteredConnectors).map((c) => (
            <div key={c._id} className={`card p-5 space-y-3 transition-all ${!c.is_active ? 'opacity-60' : ''}`}>
              {/* Card header */}
              <div className="flex items-start gap-3">
                <span className={`mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ${c.is_active ? 'bg-green-500' : 'bg-gray-400'}`} />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 dark:text-gray-100 truncate">{c.name}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mt-0.5">{c.description}</p>
                </div>
              </div>

              {/* Badges row */}
              <div className="flex flex-wrap gap-2 text-xs">
                <span className={`px-2 py-0.5 rounded-full font-medium ${METHOD_COLORS[c.method]}`}>{c.method}</span>
                {(c.method === 'POST' || c.method === 'PUT') && (
                  <span title="Write operation — modifies external data" className="text-amber-500">
                    <AlertTriangle className="w-3.5 h-3.5" />
                  </span>
                )}
                {c.agent_scope === 'all' ? (
                  <span className="px-2 py-0.5 rounded-full font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400">Global</span>
                ) : (
                  <span className="px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                    {c.agent_ids.length} Agent{c.agent_ids.length !== 1 ? 's' : ''}
                  </span>
                )}
                {(c.render_as === 'product_cards' || c.render_as === 'order_cards') && (
                  <span className="flex items-center gap-1 px-2 py-0.5 rounded-full font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-400">
                    <Sparkles className="w-3 h-3" />
                    {RENDER_AS_LABELS[c.render_as]}
                  </span>
                )}
                <span className="flex items-center gap-1 text-gray-400 dark:text-gray-500">
                  <Activity className="w-3 h-3" />
                  {c.call_count} call{c.call_count !== 1 ? 's' : ''}
                  {c.last_called_at && <> · {timeAgo(c.last_called_at)}</>}
                </span>
              </div>

              {/* Last error */}
              {c.last_error && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3 shrink-0" />
                  <span className="truncate">{c.last_error}</span>
                </p>
              )}

              {/* Actions */}
              <div className="flex items-center gap-2 pt-1 border-t border-gray-100 dark:border-gray-700">
                <button
                  onClick={() => handleToggle(c)}
                  disabled={togglingId === c._id}
                  className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-primary transition-colors"
                >
                  {togglingId === c._id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : c.is_active ? (
                    <ToggleRight className="w-5 h-5 text-green-500" />
                  ) : (
                    <ToggleLeft className="w-5 h-5" />
                  )}
                  {c.is_active ? 'Active' : 'Inactive'}
                </button>
                <div className="flex-1" />
                <button
                  onClick={() => openEdit(c)}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-primary transition-colors px-2 py-1 rounded"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  Edit
                </button>
                <button
                  onClick={() => setDeleteTarget(c)}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-500 transition-colors px-2 py-1 rounded"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Slide-over Panel ───────────────────────────────────────────────── */}
      {showForm && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/40 z-40" onClick={closeForm} />

          {/* Panel */}
          <div className="fixed inset-y-0 right-0 z-50 w-full sm:w-[520px] bg-white dark:bg-gray-900 shadow-2xl flex flex-col overflow-hidden">
            {/* Panel header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {editingConnector ? 'Edit Connector' : 'New Connector'}
              </h2>
              <button onClick={closeForm} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

              {/* ── Basic Info ── */}
              <section className="space-y-4">
                <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Basic Info</h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Get Order Status"
                    className="input-field w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    AI Decision Prompt
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">
                    This is what the AI reads to decide when to call this connector. Be specific.
                  </p>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={4}
                    placeholder="Call this when the user asks about their order status and provides an order ID."
                    className="input-field w-full resize-none"
                  />
                </div>

                {/* Returns Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Returns
                    <span className="ml-1.5 text-xs text-gray-400 font-normal">(optional)</span>
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">
                    Describe what fields this connector returns. Helps the AI understand available data for multi-step lookups.
                    {savedConnector && savedConnector.projection.length > 0 && !returnsDescription && (
                      <button
                        type="button"
                        onClick={() => setReturnsDescription(`Returns: ${savedConnector.projection.join(', ')}.`)}
                        className="ml-2 text-primary hover:underline text-xs"
                      >
                        Auto-fill from projection
                      </button>
                    )}
                  </p>
                  <input
                    type="text"
                    value={returnsDescription}
                    onChange={(e) => setReturnsDescription(e.target.value)}
                    placeholder="e.g. Returns customer_id, account_status, and subscription_tier."
                    className="input-field w-full"
                  />
                </div>

                {/* Depends On */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Dependency Hint
                    <span className="ml-1.5 text-xs text-gray-400 font-normal">(optional)</span>
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">
                    Only fill this if this connector requires data from another connector's output first.
                  </p>
                  <input
                    type="text"
                    value={dependsOn}
                    onChange={(e) => setDependsOn(e.target.value)}
                    placeholder="e.g. Requires customer_id — call Lookup Customer By Email first if not available."
                    className="input-field w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Endpoint URL</label>
                  <input
                    type="url"
                    value={endpoint}
                    onChange={(e) => setEndpoint(e.target.value)}
                    placeholder="https://api.example.com/orders/{order_id}"
                    className="input-field w-full font-mono text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Method</label>
                  <select value={method} onChange={(e) => setMethod(e.target.value as typeof method)} className="input-field w-full">
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                    <option value="PUT">PUT</option>
                  </select>
                </div>

                {(method === 'POST' || method === 'PUT') && (
                  <div className="flex gap-2.5 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-300">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <p>
                      <span className="font-semibold">Write operation warning:</span> POST and PUT requests may create or modify data in your CRM or external system. Ensure this is intentional and that your AI agent is only calling this connector when appropriate. If you need help, <a href={SUPPORT_MAILTO} className="underline hover:text-amber-900 dark:hover:text-amber-200">contact AskProdigy AI support</a>.
                    </p>
                  </div>
                )}
              </section>

              {/* ── Authentication ── */}
              <section className="space-y-4">
                <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Authentication</h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Auth Type</label>
                  <select value={authType} onChange={(e) => { setAuthType(e.target.value as typeof authType); setAuthReset(false); }} className="input-field w-full">
                    <option value="none">None</option>
                    <option value="bearer">Bearer Token</option>
                    <option value="api_key">API Key</option>
                    <option value="basic">Basic Auth</option>
                  </select>
                </div>

                {showAuth && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Auth Source</label>
                    <select
                      value={authSource}
                      onChange={(e) => setAuthSource(e.target.value as 'encrypted' | 'input_param')}
                      className="input-field w-full"
                    >
                      <option value="encrypted">Saved Token / Key</option>
                      <option value="input_param">From Input Param (dynamic token)</option>
                    </select>
                  </div>
                )}

                {showAuth && authSource === 'input_param' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Auth Param Name</label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">
                      Name of the input parameter that holds the auth token at runtime.
                    </p>
                    <input
                      type="text"
                      value={authParamName}
                      onChange={(e) => setAuthParamName(e.target.value)}
                      placeholder="e.g. bearer_token"
                      className="input-field w-full"
                    />
                  </div>
                )}

                {showAuth && authSource !== 'input_param' && editingConnector?.has_auth && !authReset ? (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                    <span className="flex-1 font-mono text-sm text-gray-500 dark:text-gray-400">
                      {editingConnector.auth_masked || '••••••••'}
                    </span>
                    <button
                      onClick={() => setAuthReset(true)}
                      className="text-xs text-primary hover:underline"
                    >
                      Reset
                    </button>
                  </div>
                ) : showAuth && authSource !== 'input_param' && (
                  <div className="space-y-3">
                    {authType === 'bearer' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Token</label>
                        <input type="password" value={authValue} onChange={(e) => setAuthValue(e.target.value)} placeholder="Bearer token" className="input-field w-full" />
                      </div>
                    )}
                    {authType === 'api_key' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Header Name</label>
                          <input type="text" value={headerName} onChange={(e) => setHeaderName(e.target.value)} placeholder="X-Api-Key" className="input-field w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Key Value</label>
                          <input type="password" value={authValue} onChange={(e) => setAuthValue(e.target.value)} placeholder="API key" className="input-field w-full" />
                        </div>
                      </>
                    )}
                    {authType === 'basic' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Username</label>
                          <input type="text" value={basicUsername} onChange={(e) => setBasicUsername(e.target.value)} className="input-field w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Password</label>
                          <input type="password" value={basicPassword} onChange={(e) => setBasicPassword(e.target.value)} className="input-field w-full" />
                        </div>
                      </>
                    )}
                  </div>
                )}
              </section>

              {/* ── Custom Headers ── */}
              <section className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Custom Headers</h3>
                  <button
                    onClick={() => setCustomHeaders(prev => [...prev, { name: '', value: '' }])}
                    className="text-xs text-primary hover:underline flex items-center gap-1"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add Header
                  </button>
                </div>

                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Static headers sent with every request (e.g. <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">X-Shopify-Api-Version</code>).
                </p>

                {customHeaders.length === 0 && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 italic">No custom headers.</p>
                )}

                {customHeaders.map((h, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <input
                      type="text"
                      value={h.name}
                      onChange={(e) => setCustomHeaders(prev => prev.map((x, i) => i === idx ? { ...x, name: e.target.value } : x))}
                      placeholder="Header-Name"
                      className="input-field flex-1 text-sm py-1.5 font-mono"
                    />
                    <input
                      type="text"
                      value={h.value}
                      onChange={(e) => setCustomHeaders(prev => prev.map((x, i) => i === idx ? { ...x, value: e.target.value } : x))}
                      placeholder="value"
                      className="input-field flex-1 text-sm py-1.5"
                    />
                    <button
                      onClick={() => setCustomHeaders(prev => prev.filter((_, i) => i !== idx))}
                      className="text-gray-400 hover:text-red-500 transition-colors shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </section>

              {/* ── Signed-in User Identity ── */}
              <section className="space-y-3">
                <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Signed-in User Identity</h3>

                <label className="flex items-start gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={forwardIdentityToken}
                    onChange={(e) => setForwardIdentityToken(e.target.checked)}
                    className="rounded mt-0.5 shrink-0"
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Forward the signed-in user's identity token
                    </span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Only for APIs that accept our partner identity tokens. The token is sent as a header alongside this connector's own credentials, so the API answers only for the user who is signed in. If the user isn't signed in, the connector still calls the API without the token, and the API replies with a sign-in link that the agent passes on.
                    </span>
                  </span>
                </label>

                {forwardIdentityToken && (
                  <div className="space-y-3 pl-2 border-l-2 border-primary/20">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Header name</label>
                      <input
                        type="text"
                        value={identityHeaderName}
                        onChange={(e) => setIdentityHeaderName(e.target.value)}
                        placeholder="e.g. X-User-Identity"
                        className="input-field w-full text-sm font-mono"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Leave blank to use the platform's standard header.
                      </p>
                    </div>
                  </div>
                )}
              </section>

              {/* ── Request Body ── */}
              {(method === 'POST' || method === 'PUT') && (
                <section className="space-y-4">
                  <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Request Body</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Body Type</label>
                    <select value={bodyType} onChange={(e) => setBodyType(e.target.value as typeof bodyType)} className="input-field w-full">
                      <option value="none">None</option>
                      <option value="json">JSON</option>
                      <option value="form">Form Data</option>
                    </select>
                  </div>
                  {bodyType !== 'none' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Body Template</label>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">
                        Use <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">&#123;&#123;param_name&#125;&#125;</code> to insert input values.
                      </p>
                      <textarea
                        value={bodyTemplate}
                        onChange={(e) => setBodyTemplate(e.target.value)}
                        rows={5}
                        placeholder={'{\n  "order_id": "{{order_id}}"\n}'}
                        className="input-field w-full resize-none font-mono text-sm"
                      />
                    </div>
                  )}
                </section>
              )}

              {/* ── Input Parameters ── */}
              <section className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Input Parameters</h3>
                  <button
                    onClick={() => setInputParams(prev => [...prev, { ...EMPTY_PARAM }])}
                    className="text-xs text-primary hover:underline flex items-center gap-1"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add Parameter
                  </button>
                </div>

                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Use <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">&#123;param_name&#125;</code> in your endpoint URL to insert the value.
                </p>

                {inputParams.length === 0 && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 italic">No parameters yet.</p>
                )}

                {inputParams.map((p, idx) => (
                  <div key={idx} className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 space-y-2 bg-gray-50 dark:bg-gray-800/50">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={p.name}
                        onChange={(e) => updateParam(idx, 'name', e.target.value)}
                        placeholder="param_name"
                        className="input-field flex-1 text-sm py-1.5 font-mono"
                      />
                      <select
                        value={p.type}
                        onChange={(e) => updateParam(idx, 'type', e.target.value)}
                        className="input-field text-sm py-1.5 w-28"
                      >
                        <option value="string">String</option>
                        <option value="integer">Integer</option>
                        <option value="number">Number</option>
                      </select>
                      <button onClick={() => removeParam(idx)} className="text-gray-400 hover:text-red-500 transition-colors shrink-0">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={p.required}
                          onChange={(e) => updateParam(idx, 'required', e.target.checked)}
                          className="rounded"
                        />
                        Required
                      </label>
                      <input
                        type="text"
                        value={p.description}
                        onChange={(e) => updateParam(idx, 'description', e.target.value)}
                        placeholder="Description"
                        className="input-field flex-1 text-sm py-1.5"
                      />
                    </div>

                    {/* Advanced toggle */}
                    <div className="flex items-center gap-2 mt-1">
                      <button
                        type="button"
                        onClick={() => setOpenAdvancedParams(prev => {
                          const next = new Set(prev);
                          next.has(idx) ? next.delete(idx) : next.add(idx);
                          return next;
                        })}
                        className="flex items-center gap-1 text-xs text-gray-400 hover:text-primary transition-colors"
                      >
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openAdvancedParams.has(idx) ? 'rotate-180' : ''}`} />
                        Advanced
                      </button>
                      {(p.source !== 'customer' || p.ask_prompt || p.example || p.static_value) && (
                        <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                      )}
                    </div>

                    {openAdvancedParams.has(idx) && (
                      <div className="mt-2 space-y-2 pl-2 border-l-2 border-primary/20">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5">Source</label>
                          <select
                            value={p.source}
                            onChange={(e) => {
                              const source = e.target.value as InputParam['source'];
                              // Default the claim on entry so a saved identity
                              // param always names one; drop it on exit.
                              setInputParams(prev => prev.map((x, i) => i === idx
                                ? { ...x, source, identity_claim: source === 'identity' ? (x.identity_claim ?? 'email') : null }
                                : x));
                            }}
                            className="input-field w-full text-sm py-1.5"
                          >
                            <option value="customer">Customer Input</option>
                            <option value="context">From Context</option>
                            <option value="static">Static Value</option>
                            <option value="identity">Verified user identity</option>
                          </select>
                        </div>

                        {p.source === 'identity' && (
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5">Identity Field</label>
                            <select
                              value={p.identity_claim ?? 'email'}
                              onChange={(e) => updateParam(idx, 'identity_claim', e.target.value)}
                              className="input-field w-full text-sm py-1.5"
                            >
                              {IDENTITY_CLAIM_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                              Filled from the signed-in user's verified identity, never from the conversation — the AI cannot supply or change it.
                            </p>
                          </div>
                        )}

                        {p.source === 'customer' && (
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5">Ask Prompt</label>
                            <input
                              type="text"
                              value={p.ask_prompt}
                              onChange={(e) => updateParam(idx, 'ask_prompt', e.target.value)}
                              placeholder="May I have your email address?"
                              className="input-field w-full text-sm py-1.5"
                            />
                          </div>
                        )}

                        {(p.source === 'customer' || p.source === 'context') && (
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5">Example / Format</label>
                            <input
                              type="text"
                              value={p.example}
                              onChange={(e) => updateParam(idx, 'example', e.target.value)}
                              placeholder="e.g. user@example.com"
                              className="input-field w-full text-sm py-1.5"
                            />
                          </div>
                        )}

                        {p.source === 'static' && (
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5">Static Value</label>
                            <input
                              type="password"
                              value={p.static_value}
                              onChange={(e) => updateParam(idx, 'static_value', e.target.value)}
                              placeholder="Hardcoded value (masked)"
                              className="input-field w-full text-sm py-1.5"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </section>

              {/* ── Agent Scope ── */}
              <section className="space-y-3">
                <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Agent Scope</h3>

                <div className="space-y-2">
                  {(['all', 'selected'] as const).map((val) => (
                    <label key={val} className="flex items-center gap-2.5 cursor-pointer text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="radio"
                        name="agentScope"
                        value={val}
                        checked={agentScope === val}
                        onChange={() => setAgentScope(val)}
                      />
                      {val === 'all' ? 'All agents in workspace' : 'Specific agents'}
                    </label>
                  ))}
                </div>

                {agentScope === 'selected' && (
                  <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 space-y-2 max-h-40 overflow-y-auto">
                    {agents.length === 0 ? (
                      <p className="text-xs text-gray-400">No agents found.</p>
                    ) : agents.map((a) => (
                      <label key={a.id} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedAgentIds.includes(a.id) || selectedAgentIds.includes(a.agent_id)}
                          onChange={() => toggleAgentId(a.id)}
                          className="rounded"
                        />
                        {a.config?.agent_name || a.id}
                      </label>
                    ))}
                  </div>
                )}
              </section>

              {/* ── Test Playground ── */}
              {savedConnector && (
                <section className="space-y-4">
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
                      Test Playground
                    </h3>

                    {/* Param inputs */}
                    {savedConnector.input_params.length > 0 && (
                      <div className="space-y-2 mb-3">
                        {savedConnector.input_params.map((p) => (
                          <div key={p.name}>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5">
                              {p.name}
                              {p.required && <span className="text-red-500 ml-0.5">*</span>}
                            </label>
                            <input
                              type={p.type === 'integer' ? 'number' : 'text'}
                              value={testInputs[p.name] || ''}
                              onChange={(e) => setTestInputs(prev => ({ ...prev, [p.name]: e.target.value }))}
                              placeholder={p.description || p.name}
                              className="input-field w-full text-sm py-1.5"
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    <button
                      onClick={handleTest}
                      disabled={testLoading}
                      className="btn-primary flex items-center gap-2 text-sm px-4 py-2"
                    >
                      {testLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                      Run Test
                    </button>

                    {/* Response */}
                    {testResult && (
                      <div className="mt-4 space-y-4">
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Response:</p>
                            <button
                              type="button"
                              onClick={() => setSavedResponse(testResult.data)}
                              className="text-xs text-green-600 dark:text-green-400 hover:underline font-medium flex items-center gap-1"
                            >
                              <Save className="w-3.5 h-3.5" />
                              Save Response
                            </button>
                          </div>
                          <pre className="bg-gray-900 text-green-400 text-xs rounded-lg p-3 overflow-x-auto whitespace-pre font-mono max-h-48">
                            {JSON.stringify(testResult, null, 2)}
                          </pre>
                        </div>

                        {/* Field Projection */}
                        <div>
                          <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                            Field Projection
                            {projectionFields.length === 0 && (
                              <span className="ml-2 text-gray-400 font-normal">(All fields will be sent to AI)</span>
                            )}
                          </p>
                          <div className="flex flex-wrap gap-x-4 gap-y-2 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                            {testResponseKeys.map((key) => (
                              <label key={key} className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={projectionFields.includes(key)}
                                  onChange={() => toggleProjectionField(key)}
                                  className="rounded"
                                />
                                <span className="font-mono text-xs">{key}</span>
                              </label>
                            ))}
                          </div>
                          <button
                            onClick={handleSaveProjection}
                            disabled={projectionSaving}
                            className="mt-2 flex items-center gap-1.5 text-sm text-primary hover:underline"
                          >
                            {projectionSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                            Save Projection
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Show existing projection if no test run yet */}
                    {!testResult && projectionFields.length > 0 && (
                      <div className="mt-4">
                        <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Saved Projection:</p>
                        <div className="flex flex-wrap gap-1.5">
                          {projectionFields.map(f => (
                            <span key={f} className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full font-mono">{f}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {!testResult && projectionFields.length === 0 && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                        Run a test to configure field projection.
                      </p>
                    )}

                    {/* Use Saved Response toggle */}
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Use Saved Response</label>
                          <p className="text-xs text-gray-500 dark:text-gray-400">When enabled, the AI uses the saved response instead of calling the API live.</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setUseSavedResponse(!useSavedResponse)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${useSavedResponse ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${useSavedResponse ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>
                      {savedResponse && (
                        <p className="text-xs text-green-600 dark:text-green-400">
                          ✓ Response saved — {Array.isArray(savedResponse) ? savedResponse.length + ' items' : typeof savedResponse === 'object' ? Object.keys(savedResponse).length + ' keys' : 'value'}
                        </p>
                      )}
                    </div>
                  </div>
                </section>
              )}

              {/* ── Card Rendering (Plan-ConnectorCardRendering.md) ── */}
              <section className="space-y-4">
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
                    Card Rendering
                  </h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Render results as</label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">
                      Show this connector's results as rich cards in the chat widget instead of plain text.
                    </p>
                    <select
                      value={renderAs}
                      onChange={(e) => { setRenderAs(e.target.value as RenderAs); setInferHint(null); }}
                      className="input-field w-full"
                    >
                      <option value="none">None (plain text)</option>
                      <option value="product_cards">Product cards</option>
                      <option value="order_cards">Order cards</option>
                    </select>
                  </div>

                  {renderAs !== 'none' && (
                    <div className="mt-4 space-y-4">
                      {!savedConnector ? (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Save the connector, then use <span className="font-medium">Run Test</span> in the
                          Test Playground to capture a sample response — the field mapping is generated from it.
                        </p>
                      ) : (
                        <>
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                            <button
                              onClick={handleGenerateMapping}
                              disabled={inferLoading}
                              className="btn-primary flex items-center justify-center gap-2 text-sm px-4 py-2 w-full sm:w-auto"
                            >
                              {inferLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                              {cardMapping ? 'Regenerate mapping' : 'Generate mapping'}
                            </button>
                            {cardMapping && (
                              <span className="text-xs text-green-600 dark:text-green-400">
                                ✓ Mapping {cardMappingSource === 'manual' ? 'adjusted manually' : 'generated'} — click {editingConnector ? 'Update' : 'Save'} to apply.
                              </span>
                            )}
                          </div>

                          {/* Usage disclaimer — each generate/regenerate is a
                              billed AI call (backend charges +1 chat credit).
                              🚨 Legacy-only: that debit rides `chat_conversations`,
                              which pooled billing retires as a billing unit
                              (`back/utils/text_pool.py:89`), so "counts as 1 chat"
                              names a unit the customer is no longer billed in.
                              The generation still costs pool tokens; quoting a
                              figure for it is the token-audit surface's job. */}
                          {showLegacyCredits && (
                            <p className="flex items-start gap-1.5 text-xs text-gray-400 dark:text-gray-500 mt-1.5">
                              <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                              <span>
                                Generating a mapping uses AI — each generation counts as 1 chat toward your usage.
                              </span>
                            </p>
                          )}

                          {!cardMapping && !inferHint && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              A field mapping is required before cards render. It is generated once from the
                              saved test response — runtime rendering is fully deterministic.
                            </p>
                          )}

                          {inferHint && (
                            <div className="flex gap-2.5 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-300">
                              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                              <p>
                                {inferHint}{' '}
                                In the Test Playground above: <span className="font-semibold">Run Test</span>,
                                then <span className="font-semibold">Save Response</span>, then{' '}
                                <span className="font-semibold">{editingConnector ? 'Update' : 'Save'}</span> the
                                connector — and generate again.
                              </p>
                            </div>
                          )}

                          {inferWarnings.length > 0 && (
                            <div className="flex gap-2.5 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-300">
                              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                              <ul className="space-y-1 list-disc list-inside">
                                {inferWarnings.map((w, i) => <li key={i}>{w}</li>)}
                              </ul>
                            </div>
                          )}

                          {/* Product page URL — Plan-ConnectorProductUrlTemplate.md.
                              Most catalog APIs return a handle/slug, not a URL, so the
                              card has nothing to link to until the storefront host is
                              recorded here. Stored inside card_mapping. */}
                          {renderAs === 'product_cards' && cardMapping && (
                            <div className="space-y-1.5">
                              <label
                                htmlFor="product-url-template"
                                className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300"
                              >
                                <Link2 className="w-3.5 h-3.5 shrink-0" />
                                Product page URL
                                <span className="font-normal text-gray-400 dark:text-gray-500">(optional)</span>
                              </label>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Where a product card links to. Wrap a field from your API in{' '}
                                <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">{'{braces}'}</code> —
                                e.g. <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">https://yourstore.com/products/{'{handle}'}</code>.
                                Leave empty if your API already returns full product URLs.
                              </p>
                              <input
                                id="product-url-template"
                                type="url"
                                inputMode="url"
                                spellCheck={false}
                                autoComplete="off"
                                value={urlTemplate}
                                onChange={(e) => handleProductUrlTemplateChange(e.target.value)}
                                placeholder="https://yourstore.com/products/{handle}"
                                className="input-field w-full font-mono text-xs"
                              />

                              {sampleItemFields.length > 0 && (
                                <p className="text-[11px] text-gray-400 dark:text-gray-500 break-words">
                                  Available fields:{' '}
                                  {sampleItemFields.map((f, i) => (
                                    <React.Fragment key={f}>
                                      {i > 0 && ', '}
                                      <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">{`{${f}}`}</code>
                                    </React.Fragment>
                                  ))}
                                </p>
                              )}

                              {urlTemplatePreview && (
                                urlTemplatePreview.ok ? (
                                  <p className="text-[11px] text-green-600 dark:text-green-400 break-all">
                                    Preview: <span className="font-mono">{urlTemplatePreview.text}</span>
                                  </p>
                                ) : (
                                  <p className="flex items-start gap-1 text-[11px] text-red-600 dark:text-red-400">
                                    <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" />
                                    <span className="break-words">{urlTemplatePreview.text}</span>
                                  </p>
                                )
                              )}

                              {mappedUrlIsNotAbsolute && !urlTemplate.trim() && (
                                <div className="flex gap-2.5 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-300">
                                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                  <p>
                                    The mapped <code className="bg-amber-100 dark:bg-amber-900/40 px-1 rounded">url</code> field
                                    holds <span className="font-semibold break-all">
                                      {String(resolvePath(sampleItem, cardMapping.fields.url) ?? '')}
                                    </span>, not a full URL. Set a Product page URL above so the cards link somewhere.
                                  </p>
                                </div>
                              )}

                              {showsNoLinkWarning && (
                                <div className="flex gap-2.5 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-300">
                                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                  <p>Product cards will render without a link.</p>
                                </div>
                              )}
                            </div>
                          )}

                          {sampleCards.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Live preview</p>
                              <p className="text-[11px] text-gray-400 dark:text-gray-500 mb-1.5">
                                Rendered with the exact card components the chat widget uses. The accent color
                                follows your agent's widget settings at runtime.
                              </p>
                              <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                                {renderAs === 'product_cards' ? (
                                  <div className="flex gap-2 overflow-x-auto pb-1">
                                    {previewCards.map((card, i) => (
                                      <ProductTile
                                        key={i}
                                        {...(card as unknown as ProductCardData)}
                                        brandColor={PREVIEW_BRAND_COLOR}
                                        isDark={isDark}
                                      />
                                    ))}
                                  </div>
                                ) : (
                                  <div className="space-y-2 max-h-96 overflow-y-auto">
                                    {sampleCards.map((card, i) => (
                                      <OrderCard
                                        key={i}
                                        data={card as unknown as OrderCardData}
                                        brandColor={PREVIEW_BRAND_COLOR}
                                        isDark={isDark}
                                      />
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {cardMapping && (
                            <div>
                              <button
                                type="button"
                                onClick={() => setShowMappingJson(v => !v)}
                                className="flex items-center gap-1 text-xs text-gray-400 hover:text-primary transition-colors"
                              >
                                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showMappingJson ? 'rotate-180' : ''}`} />
                                Adjust mapping (advanced)
                              </button>
                              {showMappingJson && (
                                <div className="mt-2 space-y-1.5">
                                  <p className="text-[11px] text-gray-400 dark:text-gray-500">
                                    Paths use dot/bracket notation (e.g. <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">data.products</code>,{' '}
                                    <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">items[0].price</code>).
                                    The preview above reflects the last generated mapping — regenerate to re-check.
                                  </p>
                                  <textarea
                                    value={mappingJson}
                                    onChange={(e) => handleMappingJsonChange(e.target.value)}
                                    rows={8}
                                    spellCheck={false}
                                    className="input-field w-full resize-y font-mono text-xs"
                                  />
                                  {mappingJsonError && (
                                    <p className="text-xs text-red-500 flex items-center gap-1">
                                      <AlertCircle className="w-3 h-3 shrink-0" />
                                      {mappingJsonError}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </section>
            </div>

            {/* Panel footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 shrink-0">
              <button onClick={closeForm} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={formLoading}
                className="btn-primary flex items-center gap-2 px-4 py-2 text-sm"
              >
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editingConnector ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Shopify Connect / Disconnect modals moved into
          <ShopifyIntegrationsBlock> (Plan-ShopifyIsolatedFrontend.md §3.1)
          so they live inside the tree-shaken Shopify-only chunk. */}

      {/* Delete Confirm */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Connector"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? This cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isDangerous
      />

      {/* Shopify shop-domain modal — Plan-ShopifyIntegrationsCleanup.md §C.
          Drives the Continue-setup CTA flow above; opens OAuth on submit. */}
      <ShopDomainModal
        isOpen={shopModalOpen}
        onClose={() => setShopModalOpen(false)}
        onSubmit={handleShopSubmit}
      />
    </div>
  );
};

export default Integrations;
